// Manages workspace AI context notes.
import { useCallback, useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import {
  getWorkspaceContextDocuments as getWorkspaceContextDocumentsQuery,
  deleteWorkspaceContextDocument as deleteDocumentMutation,
  addWorkspaceContextNote as addNoteMutation,
} from "src/queries/orgConfig.graphql";

export const useWorkspaceContextDocuments = () => {
  const { data, loading, refetch } = useQuery(
    getWorkspaceContextDocumentsQuery,
    {
      fetchPolicy: "cache-and-network",
    }
  );

  const [deleteDoc] = useMutation(deleteDocumentMutation, {
    refetchQueries: [{ query: getWorkspaceContextDocumentsQuery }],
  });

  const [addNote] = useMutation(addNoteMutation, {
    refetchQueries: [{ query: getWorkspaceContextDocumentsQuery }],
  });

  const [savingNote, setSavingNote] = useState(false);
  const [noteError, setNoteError] = useState(null);

  const contextNotes = (data?.docs ?? [])
    .slice()
    .sort(
      (a, b) =>
        new Date(b.createdAt || 0).getTime() -
        new Date(a.createdAt || 0).getTime()
    );

  const deleteNote = useCallback(
    async (id) => {
      await deleteDoc({ variables: { documentId: id } });
    },
    [deleteDoc]
  );

  const saveNote = useCallback(
    async (text) => {
      setSavingNote(true);
      setNoteError(null);
      try {
        await addNote({ variables: { text } });
      } catch (e) {
        setNoteError(e?.message || "Failed to save note. Please try again.");
        throw e;
      } finally {
        setSavingNote(false);
      }
    },
    [addNote]
  );

  const clearNoteError = useCallback(() => setNoteError(null), []);

  return {
    loading,
    contextNotes,
    savingNote,
    noteError,
    clearNoteError,
    deleteNote,
    saveNote,
    refetch,
  };
};
